import { useState, useContext } from "react";
import { useLocation, useNavigate } from "react-router";
import { trpc } from "../../lib/trpc";
import { useTheme } from "../../context/ThemeContext";
import { ProfileDraftContext } from "@/context/ProfileDraftContext";
import type { WorkspaceTab } from "@/context/ProfileDraftContext";
import { mapSubscriptionToProductTier } from "@profolio/validators/tiers";
import { Button } from "@/components/ui/button";
import { AccountMenu } from "./AccountMenu";
import {
  Home,
  FileText,
  Palette,
  Layers,
  Rocket,
  Eye,
  EyeOff,
  AlertCircle,
  Menu,
  X,
  Loader2,
  ExternalLink,
  CreditCard,
  ShieldAlert,
  Sun,
  Moon,
  LogOut,
  ChevronRight,
  Search,
  type LucideIcon,
} from "lucide-react";
import { CommandPalette } from "@/components/ui/CommandPalette";
import { Tooltip } from "@/components/ui/Tooltip";

/* ─── Types ─── */
interface NavItem {
  id: string;
  label: string;
  icon: LucideIcon;
  href?: string;
  workspaceTab?: WorkspaceTab;
  proOnly?: boolean;
}

/* ─── Unified 48px Application Header ─── */
export function UnifiedHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: session } = trpc.auth.me.useQuery();
  const { data: profile } = trpc.profile.getMyProfile.useQuery(undefined, {
    enabled: !!session,
    refetchInterval: 3000,
  });
  const { data: subscription } = trpc.billing.getMySubscription.useQuery(undefined, {
    enabled: !!session,
    refetchInterval: 3000,
  });
  const { resolvedTheme, toggleTheme } = useTheme();

  const [mobileOpen, setMobileOpen] = useState(false);

  // Safely read editor context — null when on billing/CV/admin pages (outside ProfileDraftProvider)
  const editor = useContext(ProfileDraftContext);
  const isEditorPage = location.pathname === "/profile/edit";
  const isAdmin = session?.role === "admin";

  const isSubscriptionActive = subscription?.status === "active" && (!subscription.expiresAt || new Date(subscription.expiresAt) >= new Date());
  const rawTier = (isSubscriptionActive ? subscription?.tier : null) || editor?.userTier || "starter";
  const productTier = mapSubscriptionToProductTier(rawTier);
  const isPro = productTier === "pro" || productTier === "premium";

  const logoutMutation = trpc.auth.logout.useMutation({
    onSuccess: () => {
      window.location.href = "/login";
    },
  });

  const isNavLocked = editor?.isNavigationGuardOpen || editor?.isApplyingAndNavigating;

  /* ─── Navigation Items ─── */
  const editorNavItems: NavItem[] = [
    { id: "home", label: "Home", icon: Home, workspaceTab: "home" },
    { id: "content", label: "Content", icon: FileText, workspaceTab: "content" },
    { id: "design", label: "Design", icon: Palette, workspaceTab: "design" },
    { id: "pages", label: "Pages", icon: Layers, workspaceTab: "pages", proOnly: true },
    { id: "publish", label: "Publish", icon: Rocket, workspaceTab: "publish" },
  ];

  const auxNavItems: NavItem[] = isAdmin
    ? [{ id: "admin", label: "Admin", icon: ShieldAlert, href: "/admin" }]
    : [
        { id: "editor", label: "Editor", icon: Home, href: "/profile/edit" },
        { id: "cv", label: "CV Builder", icon: FileText, href: "/profile/cv" },
        { id: "billing", label: "Billing", icon: CreditCard, href: "/profile/billing" },
      ];

  const navItems = isEditorPage ? editorNavItems.filter((i) => !i.proOnly || isPro) : auxNavItems;

  /* ─── Handlers ─── */
  const handleNavClick = (item: NavItem) => {
    if (item.workspaceTab && editor) {
      editor.requestNavigation({
        destination: item.workspaceTab,
        destinationType: "workspace",
        source: "unified_header",
      });
    } else if (item.href) {
      if (editor?.hasTransientDesignChanges) {
        editor.requestNavigation({
          destination: item.href,
          destinationType: "route",
          source: "unified_header",
        });
      } else {
        navigate(item.href);
      }
    }
    setMobileOpen(false);
  };

  const isItemActive = (item: NavItem): boolean => {
    if (item.workspaceTab && editor) return editor.activeTab === item.workspaceTab;
    if (item.href) return location.pathname.startsWith(item.href);
    return false;
  };

  /* ─── Breadcrumb ─── */
  const getBreadcrumb = (): string => {
    if (isEditorPage && editor) {
      const tabLabels: Record<WorkspaceTab, string> = {
        home: "Home",
        content: "Content",
        design: "Design",
        pages: "Pages",
        publish: "Publish",
      };
      return tabLabels[editor.activeTab] || "Editor";
    }
    if (location.pathname.startsWith("/profile/cv")) return "CV Builder";
    if (location.pathname.startsWith("/profile/billing")) return "Billing & Plan";
    if (location.pathname.startsWith("/admin")) return "Admin Console";
    return "Dashboard";
  };

  /* ─── Autosave Status ─── */
  const renderAutosaveStatus = () => {
    if (!editor) return null;
    const s = editor.autosaveStatus;
    if (s === "saving") {
      return (
        <span
          className="flex items-center gap-1.5 text-[11px] text-amber-600 dark:text-amber-400 font-medium"
          title="Saving draft changes..."
        >
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500" />
          </span>
          <span className="hidden lg:inline">Saving...</span>
        </span>
      );
    }
    if (s === "saved") {
      return (
        <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <span className="h-2 w-2 rounded-full bg-emerald-500" />
          <span className="hidden lg:inline">Saved</span>
        </span>
      );
    }
    if (s === "conflict" || s === "error") {
      return (
        <span className="flex items-center gap-1.5 text-[11px] text-destructive font-medium">
          <AlertCircle className="h-3 w-3" />
          <span className="hidden lg:inline">{s === "conflict" ? "Conflict" : "Sync issue"}</span>
        </span>
      );
    }
    return null;
  };

  return (
    <>
      <header className="sticky top-0 z-50 flex h-12 w-full items-center justify-between border-b border-border bg-background/90 backdrop-blur-md px-3 lg:px-5 shrink-0 select-none">
        {/* ── Left: Brand + Breadcrumb ── */}
        <div className="flex items-center gap-2.5 min-w-0">
          <button
            type="button"
            onClick={() => {
              if (isEditorPage && editor) {
                editor.requestNavigation({
                  destination: "home",
                  destinationType: "workspace",
                  source: "unified_header",
                });
              } else {
                navigate("/profile/edit");
              }
            }}
            className="text-base font-bold text-foreground tracking-tight flex items-center shrink-0 hover:opacity-80 transition-opacity cursor-pointer"
          >
            Profolio<span className="text-primary">.lk</span>
          </button>
          <ChevronRight className="h-3 w-3 text-muted-foreground/60 shrink-0 hidden sm:block" />
          <span className="text-xs font-medium text-muted-foreground truncate hidden sm:block max-w-[140px]">
            {getBreadcrumb()}
          </span>
        </div>

        {/* ── Center: Studio Switcher (Desktop) ── */}
        <nav className="hidden md:flex items-center gap-0.5 bg-muted/50 p-0.5 rounded-lg border border-border/60">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isItemActive(item);
            return (
              <button
                key={item.id}
                type="button"
                disabled={!!isNavLocked}
                onClick={() => handleNavClick(item)}
                title={item.label}
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium transition-all disabled:opacity-50 cursor-pointer ${
                  active
                    ? "bg-background text-foreground shadow-xs font-semibold"
                    : "text-muted-foreground hover:text-foreground hover:bg-background/50"
                }`}
              >
                <Icon className={`h-3.5 w-3.5 ${active ? "text-primary" : ""}`} />
                <span className="hidden lg:inline">{item.label}</span>
                {item.proOnly && (
                  <span className="text-[8px] font-bold text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-950/60 px-1 rounded-xs leading-tight">
                    PRO
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        {/* ── Right: Status & Actions ── */}
        <div className="flex items-center gap-2 shrink-0">
          {/* Autosave Status */}
          <div className="hidden sm:flex">{renderAutosaveStatus()}</div>

          {/* Cmd+K Quick Search Trigger */}
          <Tooltip content="Command Palette (Cmd+K / Ctrl+K)" side="bottom">
            <button
              onClick={() => window.dispatchEvent(new KeyboardEvent("keydown", { key: "k", metaKey: true }))}
              className="hidden md:flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] text-muted-foreground hover:text-foreground bg-muted/50 hover:bg-muted border border-border/50 transition-colors cursor-pointer"
            >
              <Search className="h-3 w-3" />
              <span className="font-mono text-[10px] opacity-70">⌘K</span>
            </button>
          </Tooltip>

          {/* Preview Toggle (editor only) */}
          {isEditorPage && editor && (
            <Tooltip content={editor.previewOpen ? "Hide live preview" : "Show live preview"} side="bottom">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => editor.setPreviewOpen((prev) => !prev)}
                className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground hidden sm:flex cursor-pointer"
              >
                {editor.previewOpen ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              </Button>
            </Tooltip>
          )}

          {/* View Live Site (editor only) */}
          {isEditorPage && editor?.draft?.profile?.subdomain && (
            <Tooltip content="Open published site in new tab" side="bottom">
              <a
                href={editor.publishedUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="hidden xl:flex items-center p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
              >
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </Tooltip>
          )}

          {/* Publish Button (editor only) */}
          {isEditorPage && editor && (
            <Button
              size="sm"
              onClick={() => editor.handlePublish()}
              disabled={editor.isPublishing || !!isNavLocked}
              className="h-7 px-3 text-[11px] font-medium bg-primary text-primary-foreground hover:bg-primary/90 shadow-xs gap-1.5 cursor-pointer"
            >
              {editor.isPublishing ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Rocket className="h-3 w-3" />
              )}
              <span className="hidden sm:inline">{editor.isPublishing ? "Publishing..." : "Publish"}</span>
            </Button>
          )}

          {/* Dashboard Theme Toggle */}
          <Tooltip content={`Switch to ${resolvedTheme === "dark" ? "light" : "dark"} mode`} side="bottom">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleTheme}
              className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground hidden sm:flex cursor-pointer"
            >
              {resolvedTheme === "dark" ? <Sun className="h-3.5 w-3.5 text-amber-400" /> : <Moon className="h-3.5 w-3.5" />}
            </Button>
          </Tooltip>

          {/* ── Account Menu Dropdown ── */}
          <div className="hidden sm:block">
            <AccountMenu align="right" />
          </div>

          {/* Mobile Hamburger */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setMobileOpen(!mobileOpen)}
            className="h-7 w-7 p-0 md:hidden cursor-pointer"
          >
            {mobileOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </Button>
        </div>
      </header>

      {/* ── Mobile Slide-Over Drawer ── */}
      {mobileOpen && (
        <div className="fixed inset-x-0 top-12 bottom-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/20 backdrop-blur-xs" onClick={() => setMobileOpen(false)} />
          <div className="relative bg-background border-b border-border shadow-xl p-4 space-y-1 animate-in slide-in-from-top-2 duration-150 max-h-[70vh] overflow-y-auto">
            <div className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">
              Navigation
            </div>
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isItemActive(item);
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => handleNavClick(item)}
                  className={`flex items-center gap-2.5 w-full p-2.5 rounded-lg text-sm font-medium transition-colors ${
                    active ? "bg-primary/10 text-primary font-semibold" : "hover:bg-muted text-foreground"
                  }`}
                >
                  <Icon className={`h-4 w-4 ${active ? "text-primary" : "text-muted-foreground"}`} />
                  {item.label}
                  {item.proOnly && (
                    <span className="text-[9px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full ml-auto">
                      PRO
                    </span>
                  )}
                </button>
              );
            })}

            {editor && (
              <div className="pt-3 mt-3 border-t border-border flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Status</span>
                {renderAutosaveStatus()}
              </div>
            )}

            <div className="pt-3 mt-3 border-t border-border space-y-1">
              <div className="flex items-center justify-between py-1">
                <span className="text-xs font-medium text-foreground">{profile?.fullName || "User"}</span>
                <span className="text-[10px] text-muted-foreground capitalize">{productTier}</span>
              </div>
              <button
                type="button"
                onClick={() => {
                  setMobileOpen(false);
                  logoutMutation.mutate();
                }}
                className="flex items-center gap-2 w-full p-2 rounded-lg text-sm text-destructive hover:bg-destructive/10 transition-colors"
              >
                <LogOut className="h-4 w-4" />
                Sign out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Global Command Palette */}
      <CommandPalette />
    </>
  );
}
